/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Redirect, Switch } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { Page } from 'src/core/components/Page';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { QnaMakerDeployPage } from 'src/qnaMaker/appShell/qnaMakerDeployPage/QnaMakerDeployPage';
import { QnaMakerEditPageV2 } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerEditPageV2';
import { QnaMakerEditPage } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerEditPage';
import { QnaMakerReviewSuggestionsPage } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerReviewSuggestionsPage';
import { QnaManageSourcesPage } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerManageSourcesPage';
import { QnaMakerProjectSettingsPage } from 'src/qnaMaker/appShell/QnaMakerProjectSettingsPage';
import { QnaMakerQuestionPairDetails } from 'src/qnaMaker/appShell/qnaMakerQuestionPairDetailsPage/QnaMakerQuestionPairDetails';
import { useInitializeQnaMakerProject } from 'src/qnaMaker/core/hooks/useInitializeQnaMakerProject';

export type QnaMakerProjectPageProps = { projectId: string; csAccountPath: string };

export const QnaMakerProjectPage = observer((props: QnaMakerProjectPageProps) => {
    const { projectId, csAccountPath } = props;
    const { isLoaded, isFailed } = useInitializeQnaMakerProject({ projectId, csAccountPath });
    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const { name: accountName } = rootStore.azureStore.session.csAccountStore;

    useEffect(() => {
        if (uiStore.allowlist?.length && isLoaded) {
            const questionGenerationAllowlist = uiStore.allowlist.find(l => l.feature === 'questionGeneration');
            const { haveSmartSuggestions } = rootStore.currentQnaMakerProjectStore;
            const isSmartSuggestionEnabled = questionGenerationAllowlist.resources.includes(accountName) || haveSmartSuggestions;
            rootStore.currentQnaMakerProjectStore.setIsSmartSuggestionsEnabled(isSmartSuggestionEnabled);
        }
    }, [uiStore.allowlist, isLoaded]);

    if (isFailed) {
        return <Redirect to={routeTable.qnaMaker.projectsPage()} />;
    }

    return isLoaded ? (
        <Switch>
            <PrivateRoute
                exact
                path={routeTable.qnaMaker.editPage(projectId)}
                component={FeatureToggles.QnaNewEditKBPage ? QnaMakerEditPageV2 : QnaMakerEditPage}
            />
            <PrivateRoute exact path={routeTable.qnaMaker.deployPage(projectId)} component={QnaMakerDeployPage} />
            <PrivateRoute exact path={routeTable.qnaMaker.reviewPage(projectId)} component={QnaMakerReviewSuggestionsPage} />
            <PrivateRoute exact path={routeTable.qnaMaker.settingsPage(projectId)} component={QnaMakerProjectSettingsPage} />
            <PrivateRoute exact path={routeTable.qnaMaker.manageSourcesPage(projectId)} component={QnaManageSourcesPage} />
            <PrivateRoute exact path={routeTable.qnaMaker.detailsPage(projectId)} component={QnaMakerQuestionPairDetails} />

            <Redirect from={routeTable.qnaMaker.projectPage(projectId)} to={routeTable.qnaMaker.manageSourcesPage(projectId)} />
            <NotFoundRedirect />
        </Switch>
    ) : (
        <Page title={localize(globalMessages.loading)} pageName="QnaMakerProjectPage" size="container-fluid" animateIn={false}>
            <ShimmerFactory fullWidth includeHeaderShimmer shimmerCount={10} />
        </Page>
    );
});
